/**
 * Bloco de PAGAMENTO do payload — o dinheiro, sem as faturas.
 *
 * Existe porque o ato de pagar foi desmembrado em três passos e o mesmo bloco passou a ser montado
 * em dois lugares: no Registrar Recebimento (que ainda faz pagamento + identificação de uma vez) e
 * no modal Registrar Pagamento da tela de Pagamentos (que faz só o passo 1). Duplicar significava
 * que uma regra corrigida num deles — o meio de pagamento que só existe em FINANCIAL, o `name`
 * NOT NULL caindo no documento — continuaria errada no outro.
 *
 * Quem precisa das faturas junta `invoices` ao resultado; quem não precisa manda como está.
 */
// `import type` nos dois primeiros: são só interfaces, e um import de valor faria o bundler
// carregar os componentes (e a árvore deles) para nada.
import type { PaymentTypeValue } from './PaymentTypeFields';
import type { PayerValue } from './PayerFields';
import { PaymentType } from '../../../constants/payment';
import { onlyDigits } from '../../../utils/taxIdUtils';

/**
 * Chave de negócio do pagamento, escolhida pela TELA.
 *
 * ⚠️ Gerar uma vez, ao ABRIR o formulário — não no submit. No submit, dois cliques (ou um retry
 * depois de timeout) produzem dois ids diferentes e nascem dois pagamentos: a deduplicação do
 * consumidor é por CHAVE, e chaves diferentes são registros diferentes por definição. Com o id
 * estável, reenviar o mesmo formulário é reenviar o mesmo comando, e o segundo é descartado com
 * um warning.
 *
 * ⚠️ SEMPRE devolve uma chave. `crypto.randomUUID` só existe em contexto seguro (em HTTP simples é
 * `undefined`), e devolver vazio ali fazia a proteção contra duplo envio desaparecer justamente
 * onde ninguém iria notar — o servidor sorteava a chave e cada clique virava um pagamento.
 */
export const newPaymentId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // UUIDv4 montado à mão: o schema do evento exige o FORMATO, e o que importa aqui é a chave
    // ser estável durante o preenchimento e não colidir — não ser criptograficamente forte.
    const aleatorio = (): number => {
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            return crypto.getRandomValues(new Uint8Array(1))[0] / 256;
        }
        return Math.random();
    };
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const n = (aleatorio() * 16) | 0;
        return (c === 'x' ? n : (n & 0x3) | 0x8).toString(16);
    });
};

export interface PaymentBlockInput {
    /** valor já convertido para número (a tela guarda como texto em BRL) */
    amount: number;
    /** instante ISO em UTC — use toIsoInstant(date, time, showTime) */
    paymentDate: string;
    typeFields: PaymentTypeValue;
    payer: PayerValue;
    paymentId?: string;
    notes?: string;
    receiptName?: string;
    userResponsible?: string;
}

export const buildPaymentBlock = (input: PaymentBlockInput): Record<string, any> => {
    const { typeFields: t, payer } = input;
    const financeiro = t.payment_type === PaymentType.FINANCIAL;
    const doc = onlyDigits(payer.document);

    return {
        payment_id: input.paymentId,
        amount: input.amount,
        payment_date: input.paymentDate,
        // ⚠️ meio de pagamento e conta só existem em pagamento FINANCEIRO. Confissão de dívida e
        // dação de bens vão com undefined — carimbar PIX ali inventaria um dado que ninguém
        // informou, e o painel da tela de Pagamentos separa justamente por isso.
        payment_method: financeiro ? t.payment_method : undefined,
        payment_type: t.payment_type,
        receiving_bank_account_id: financeiro ? t.receiving_bank_account : undefined,
        user_responsible: input.userResponsible || undefined,
        file_name_receipt: input.receiptName || undefined,
        payment_source: t.payment_source,
        is_subrogation: t.is_subrogation,
        pending_guarantor_refund: t.pending_guarantor_refund,
        debt_confession_contract: t.payment_type === PaymentType.DEBT_CONFESSION
            ? t.debt_confession_contract || undefined : undefined,
        asset_dation_list: t.payment_type === PaymentType.ASSET_DATION
            ? t.asset_dation_list : undefined,
        data: input.notes ? { notes: input.notes } : undefined,
        payer: {
            external_id: payer.personId || undefined,  // ausente → billing cria person sem external_id
            name: payer.name || doc,                   // name é NOT NULL no billing; fallback documento
            document_number: doc,
            email: payer.email || undefined,
            phone: payer.phone || undefined,
            address_street: payer.address_street || undefined,
            address_number: payer.address_number || undefined,
            address_complement: payer.address_complement || undefined,
            address_neighborhood: payer.address_neighborhood || undefined,
            address_city: payer.address_city || undefined,
            address_state: payer.address_state || undefined,
            address_zip_code: payer.address_zip_code || undefined,
        },
    };
};
